// Marketing → Queue: every Threads post by Hawaii day. Approve, reject, edit,
// or move one post; or select everything in the filter and act at once.
// Approving a post means it goes out on its own at its scheduled time.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorNote, Field, Note } from '../../components/ui';
import { errorText } from '../../lib/backbarApi';
import {
  addDays, chars, dayLabel, displayState, fromHstInput, hstDayKey, hstTime, hstToday, mk,
  PILLARS, pillarLabel, THREADS_MAX, toHstInput, type DisplayState, type Post,
} from '../../lib/marketing';
import { MkPage, PillarChip, StateChip } from './shared';

const STATE_OPTIONS: { value: '' | DisplayState; label: string }[] = [
  { value: '', label: 'All posts' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'posted', label: 'Posted' },
  { value: 'failed', label: 'Failed' },
];

type Flash = { tone: 'good' | 'warn'; text: string } | null;

export function MarketingQueue() {
  const [params, setParams] = useSearchParams();
  const state = (params.get('state') ?? '') as '' | DisplayState;
  const pillar = params.get('pillar') ?? '';
  const source = params.get('source') ?? '';
  const day = params.get('day') ?? '';
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const [posts, setPosts] = useState<Post[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);
  const [bulkStart, setBulkStart] = useState(() => addDays(hstToday(), 1));
  const reload = () => setTick((t) => t + 1);

  useEffect(() => {
    let alive = true;
    mk.queue({ limit: 1000 })
      .then((p) => { if (alive) { setPosts(p); setErr(null); } })
      .catch((e) => alive && setErr(e));
    return () => { alive = false; };
  }, [tick]);

  const sources = useMemo(() => [...new Set([...(posts ?? []).map((p) => p.source), source].filter(Boolean))].sort() as string[], [posts, source]);

  const filtered = useMemo(() => (posts ?? [])
    .filter((p) => !state || displayState(p) === state)
    .filter((p) => !pillar || p.pillar === pillar)
    .filter((p) => !source || p.source === source)
    .filter((p) => !day || (p.scheduled_at && hstDayKey(p.scheduled_at) === day))
    .sort((a, b) => (a.scheduled_at ?? '9').localeCompare(b.scheduled_at ?? '9')), [posts, state, pillar, source, day]);

  const groups = useMemo(() => {
    const m = new Map<string, Post[]>();
    for (const p of filtered) {
      const k = p.scheduled_at ? hstDayKey(p.scheduled_at) : 'unscheduled';
      m.set(k, [...(m.get(k) ?? []), p]);
    }
    return [...m.entries()];
  }, [filtered]);

  // Selection only ever holds rows in the current filter.
  useEffect(() => {
    const visible = new Set(filtered.map((p) => p.id));
    setSelected((s) => { const n = new Set([...s].filter((id) => visible.has(id))); return n.size === s.size ? s : n; });
  }, [filtered]);

  const actionable = filtered.filter((p) => !p.posted_at);
  const allChecked = actionable.length > 0 && actionable.every((p) => selected.has(p.id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(actionable.map((p) => p.id)));
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true); setFlash(null);
    try { await fn(); setFlash({ tone: 'good', text: label }); reload(); }
    catch (e) { setFlash({ tone: 'warn', text: errorText(e) }); }
    finally { setBusy(false); }
  }
  const ids = [...selected];
  const n = ids.length;
  const plural = (k: number) => `${k} ${k === 1 ? 'post' : 'posts'}`;

  return (
    <MkPage title="Queue" blurb="Every Threads post, by day, in Hawaii time. Approved posts go out on their own at the time shown.">
      <section className="lp-section">
        <div className="lp-section-head"><h2 className="lp-section-title">Filter</h2>{posts && <span className="bk-card-sub">{filtered.length} of {posts.length} posts</span>}</div>
        <div className="mk-filters">
          <Field label="State">
            <select className="lp-inp" value={state} onChange={(e) => setParam('state', e.target.value)}>
              {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Pillar">
            <select className="lp-inp" value={pillar} onChange={(e) => setParam('pillar', e.target.value)}>
              <option value="">All pillars</option>
              {PILLARS.map((p) => <option key={p} value={p}>{pillarLabel(p)}</option>)}
            </select>
          </Field>
          <Field label="Source">
            <select className="lp-inp" value={source} onChange={(e) => setParam('source', e.target.value)}>
              <option value="">All sources</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Day">
            <input type="date" className="lp-inp" value={day} onChange={(e) => setParam('day', e.target.value)} />
          </Field>
          {(state || pillar || source || day) && (
            <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</button>
          )}
        </div>
        <div className="mk-bulk">
          <label className="mk-check">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} disabled={actionable.length === 0} />
            Select all in this filter
          </label>
          {n > 0 && <span className="mk-bulk-count">{plural(n)} selected</span>}
          <button type="button" className="lp-btn lp-btn--primary" disabled={!n || busy} onClick={() => void run(`Approved ${plural(n)}. They post on schedule.`, () => mk.setState(ids, 'approved'))}>Approve selected</button>
          <button type="button" className="lp-btn lp-btn--danger" disabled={!n || busy} onClick={() => void run(`Rejected ${plural(n)}.`, () => mk.setState(ids, 'rejected'))}>Reject selected</button>
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="lp-btn" disabled={!n || busy || !bulkStart} onClick={() => void run(`Rescheduled ${plural(n)} from ${dayLabel(bulkStart, true)}.`, () => mk.bulkSchedule(ids, bulkStart))}>Reschedule from</button>
            <input type="date" className="lp-inp" aria-label="Reschedule starting on" value={bulkStart} onChange={(e) => setBulkStart(e.target.value)} />
          </span>
        </div>
        {flash && <Note tone={flash.tone}>{flash.text}</Note>}
      </section>

      <ErrorNote error={err} notConfigured="Marketing is not set up on the server yet." />
      {!posts && !err && <p className="lp-empty">Loading…</p>}
      {posts && filtered.length === 0 && <p className="lp-empty">No posts match this filter.</p>}

      {groups.map(([key, list]) => (
        <section key={key} className="bk-card mk-day">
          <div className="mk-day-head">
            <h2 className="mk-day-title">{key === 'unscheduled' ? 'No time set' : `${key === hstToday() ? 'Today · ' : ''}${dayLabel(key, true)}`}</h2>
            <span className="bk-card-sub">{plural(list.length)}</span>
          </div>
          {list.map((p) => (
            <PostRow key={p.id} post={p} checked={selected.has(p.id)} onCheck={() => toggle(p.id)} onChanged={reload} />
          ))}
        </section>
      ))}
    </MkPage>
  );
}

function PostRow({ post, checked, onCheck, onChanged }: { post: Post; checked: boolean; onCheck: () => void; onChanged: () => void }) {
  const st = displayState(post);
  const [mode, setMode] = useState<'view' | 'edit' | 'move'>('view');
  const [body, setBody] = useState(post.body);
  const [when, setWhen] = useState(toHstInput(post.scheduled_at));
  const [link, setLink] = useState(post.link_url ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const count = chars(body);
  const over = count > THREADS_MAX;

  const open = (m: 'edit' | 'move') => { setBody(post.body); setWhen(toHstInput(post.scheduled_at)); setLink(post.link_url ?? ''); setErr(null); setMode(m); };

  async function act(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try { await fn(); setMode('view'); onChanged(); }
    catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  }

  async function saveEdit() {
    const patch: { body?: string; scheduled_at?: string; link_url?: string } = {};
    if (body.trim() !== post.body) patch.body = body.trim();
    if (when && when !== toHstInput(post.scheduled_at)) patch.scheduled_at = fromHstInput(when);
    if (link.trim() !== (post.link_url ?? '')) patch.link_url = link.trim();
    if (Object.keys(patch).length === 0) { setMode('view'); return; }
    await act(() => mk.update(post.id, patch));
  }

  const eng = post.engagement ?? {};
  const engBits = (['likes', 'replies', 'reposts', 'quotes', 'views'] as const)
    .filter((k) => eng[k] != null).map((k) => `${Number(eng[k]).toLocaleString('en-US')} ${k}`);

  return (
    <div className={`mk-post mk-post--${st}`}>
      <div>
        {!post.posted_at && <input type="checkbox" className="mk-row-check" aria-label="Select this post" checked={checked} onChange={onCheck} style={{ width: 18, height: 18, accentColor: 'var(--bk-green)', marginTop: 2 }} />}
      </div>
      <div className="mk-post-time">{hstTime(post.posted_at ?? post.scheduled_at)}</div>
      <div className="mk-post-main">
        <p className="mk-post-body">{post.body}</p>
        <div className="mk-post-meta">
          <StateChip state={st} />
          <PillarChip pillar={post.pillar} />
          {post.link_url && <a href={post.link_url} target="_blank" rel="noopener noreferrer">Link</a>}
          {post.source && <span>{post.source}</span>}
          {post.source_ref && <span title="Where the fact came from">· {post.source_ref}</span>}
          <span>· {chars(post.body)} chars</span>
          {st === 'approved' && !post.auto_post && <span>· held (will not auto-post)</span>}
        </div>
        {post.posted_at && (
          <div className="mk-post-meta" style={{ marginTop: 4 }}>
            {post.permalink && <a href={post.permalink} target="_blank" rel="noopener noreferrer">View on Threads</a>}
            {engBits.length > 0 && <span>{engBits.join(' · ')}</span>}
          </div>
        )}
        {post.error && !post.posted_at && <p className="mk-post-err">Last try failed{post.attempts ? ` (${post.attempts} of 3)` : ''}: {post.error}</p>}

        {!post.posted_at && mode === 'view' && (
          <div className="mk-post-actions">
            {st !== 'approved' && <button type="button" className="lp-btn lp-btn--small lp-btn--primary" disabled={busy} onClick={() => void act(() => mk.setState([post.id], 'approved'))}>Approve</button>}
            {st !== 'rejected' && <button type="button" className="lp-btn lp-btn--small lp-btn--danger" disabled={busy} onClick={() => void act(() => mk.setState([post.id], 'rejected'))}>Reject</button>}
            {st === 'rejected' && <button type="button" className="lp-btn lp-btn--small" disabled={busy} onClick={() => void act(() => mk.setState([post.id], 'draft'))}>Back to draft</button>}
            <button type="button" className="lp-btn lp-btn--small" disabled={busy} onClick={() => open('edit')}>Edit</button>
            <button type="button" className="lp-btn lp-btn--small" disabled={busy} onClick={() => open('move')}>Reschedule</button>
          </div>
        )}

        {mode === 'edit' && (
          <div className="mk-edit">
            <Field label="Post">
              <textarea className="lp-ta" value={body} onChange={(e) => setBody(e.target.value)} aria-describedby={`count-${post.id}`} />
            </Field>
            <div id={`count-${post.id}`} className={`mk-count${over ? ' mk-count--over' : count > THREADS_MAX - 40 ? ' mk-count--near' : ''}`}>
              {count} / {THREADS_MAX}{over ? ` · ${count - THREADS_MAX} over` : ''}
            </div>
            <div className="mk-edit-grid">
              <Field label="Time (Hawaii)"><input type="datetime-local" className="lp-inp" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
              <Field label="Link (optional)" hint="Starts with https://"><input type="url" className="lp-inp" value={link} placeholder="https://rootegra.com" onChange={(e) => setLink(e.target.value)} /></Field>
            </div>
            <div className="lp-row">
              <button type="button" className="lp-btn lp-btn--primary" disabled={busy || over || !body.trim()} onClick={() => void saveEdit()}>{busy ? 'Saving…' : 'Save'}</button>
              <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={() => setMode('view')}>Cancel</button>
            </div>
          </div>
        )}

        {mode === 'move' && (
          <div className="mk-edit">
            <div className="lp-row" style={{ alignItems: 'flex-end' }}>
              <Field label="New time (Hawaii)"><input type="datetime-local" className="lp-inp" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
              <div className="lp-row" style={{ marginBottom: 10 }}>
                <button type="button" className="lp-btn lp-btn--primary" disabled={busy || !when} onClick={() => void act(() => mk.update(post.id, { scheduled_at: fromHstInput(when) }))}>{busy ? 'Saving…' : 'Move post'}</button>
                <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={() => setMode('view')}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {err && <Note tone="warn">{err}</Note>}
      </div>
    </div>
  );
}
